
// ABOUTME: Component for managing current subscription and billing information
// ABOUTME: Shows subscription status, billing history, and management options

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSubscriptions } from '@/hooks/useSubscriptions';
import { Calendar, CreditCard, Download, RefreshCw } from 'lucide-react';

const BillingManagement = () => {
  const { 
    subscriptionStatus, 
    statusLoading, 
    createPortal, 
    createPortalLoading,
    refreshSubscriptionStatus 
  } = useSubscriptions();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatAmount = (amount: number, currency: string = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  if (statusLoading) {
    return (
      <div className="space-y-6">
        <Card className="animate-pulse">
          <CardHeader className="h-20 bg-gray-200" />
          <CardContent className="h-32 bg-gray-100" />
        </Card>
      </div>
    );
  }

  if (!subscriptionStatus?.hasSubscription) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            No Active Subscription
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-4">
            You don't have an active subscription. Choose a plan to get started with Sourcerer AI.
          </p>
          <Button onClick={() => refreshSubscriptionStatus()} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Status
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Subscription */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Current Subscription
            </CardTitle>
            <Button 
              onClick={() => createPortal()}
              disabled={createPortalLoading}
              variant="outline"
              size="sm"
            >
              Manage Subscription
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {subscriptionStatus.subscriptions.map((subscription, index) => (
              <div key={subscription.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-semibold">{subscription.plan_name}</h3>
                    <p className="text-sm text-gray-600">
                      {formatAmount(subscription.amount, subscription.currency)} per month
                    </p>
                  </div>
                  <Badge variant={subscription.status === 'active' ? 'default' : 'secondary'}>
                    {subscription.status}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Renews: {formatDate(subscription.current_period_end)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <CreditCard className="w-8 h-8 mx-auto mb-2 text-blue-500" />
              <h3 className="font-semibold mb-1">Update Payment</h3>
              <p className="text-sm text-gray-600 mb-3">Change your payment method</p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => createPortal()}
                disabled={createPortalLoading}
              >
                Update
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <Download className="w-8 h-8 mx-auto mb-2 text-green-500" />
              <h3 className="font-semibold mb-1">Download Invoices</h3>
              <p className="text-sm text-gray-600 mb-3">Access your billing history</p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => createPortal()}
                disabled={createPortalLoading}
              >
                View History
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 mx-auto mb-2 text-purple-500" />
              <h3 className="font-semibold mb-1">Refresh Status</h3>
              <p className="text-sm text-gray-600 mb-3">Update subscription info</p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => refreshSubscriptionStatus()}
              >
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BillingManagement;
